import React, { useMemo } from 'react';
import {
  HStack, Box, FlatList, Text,
} from '@gluestack-ui/themed';
import { useFormContext, useWatch, FieldErrors } from 'react-hook-form';

import AttachmentItem from '@/student/task/components/Attachments/AttachmentItem';
import AttachmentMenu from '@/student/task/components/Attachments/AttachmentMenu';

import { countWords, wordCountBounds } from '@/common/utils/wordCount';
import {
  extractDimensionComments,
  getFlatEvaluationDimensions,
} from '@/common/utils/evaluation/getFlatEvaluationDimensions';
import { useEvaluationGradingConfig } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';
import { taskRequiresEvaluation } from '@/common/utils/taskEvaluationUtils';
import { useAutosave } from '@/common/hooks/forms/useAutosave';
import type { Task } from '@/common/api/models/task';
import type { EventKind } from '@/common/api/models/eventKind';
import type { HighlightSegment } from '@/common/api/models/highlightSegment';
import type { Attachment } from '@/common/api/models/attachment';

import type { DimensionCriterion } from './components/types';

import HighlightedSubmissionText from './HighlightedSubmissionText';
import ElementHeader from './ElementHeader';
import TaskContextPreview from './components/TaskContextPreview';
import EvaluationCard from './components/EvaluationCard';
import OralExamEvaluationPanel from './components/OralExamEvaluationPanel';
import { isOralExamTask, shouldHideOralExamSubmissionBlob } from '../../utils/evaluationFormMapping';

import { useLecturerEvaluationController } from '../../hooks/useLecturerEvaluationController';
import { computeApplicableDims } from './computeApplicableDims';

interface SubmissionElementProps {
  task: Task;
  eventKinds?: EventKind[];
  selectedModuleSubmission: {
    userId: string;
    courseId: string;
    moduleId: string;
    userName?: string;
  };
  isExpanded: boolean;
  isDisabled: boolean;
  isReadOnly: boolean;
  /** True while module-level autograde is running; locks all editable fields. */
  isAutograding?: boolean;
  toggleElement: () => void;
}

const SubmissionElement: React.FC<SubmissionElementProps> = ({
  task,
  eventKinds = [],
  selectedModuleSubmission,
  isExpanded,
  isDisabled,
  isReadOnly,
  isAutograding = false,
  toggleElement,
}) => {
  const {
    control,
    formState: { isSubmitted, errors, dirtyFields },
    getValues,
    resetField,
  } = useFormContext();

  const { evalDimensionKeys } = useEvaluationGradingConfig();

  const storedDimGrades = getFlatEvaluationDimensions(task.submission?.evaluation);
  const applicableDims = computeApplicableDims(task, storedDimGrades, evalDimensionKeys);

  // New Domain Controller for saving
  const { saveEvaluation, status: controllerStatus, errorMessage: controllerError } = useLecturerEvaluationController({
    evaluationUserId: selectedModuleSubmission.userId,
    evaluationCourseId: selectedModuleSubmission.courseId,
    evaluationModuleId: selectedModuleSubmission.moduleId,
    taskId: task.id,
    applicableDims,
  });

  // Watch form path for changes
  const watchFormPath = `taskEvaluations.${task.id}`;
  const taskFormData = useWatch({
    control,
    name: watchFormPath,
  });

  // Watch highlighting config from form state
  const highlightConfig = useWatch({
    control,
    name: `taskEvaluations.${task.id}.feedback.highlighting`,
    defaultValue: task.submission?.feedback?.highlighting || null,
  }) as Record<string, HighlightSegment[]> | null;

  // Same RHF pitfall as DimRating (39224ab4a, 2026-08-21): defaultValue on
  // useWatch ignores form defaults until the leaf is registered. Comment
  // leaves have no input, so defaultValue: null hid AI Analysis after
  // 80664c3f0 stopped resetting taskEvaluations on every tasks GET.
  const evaluationDimensions = useWatch({
    control,
    name: `taskEvaluations.${task.id}.evaluation.dimensions`,
  }) as Record<string, { comment?: string | null }> | undefined;

  const autogradeReasoning = useMemo(
    () => extractDimensionComments(evaluationDimensions) ?? extractDimensionComments(storedDimGrades),
    [evaluationDimensions, storedDimGrades],
  );

  // Parse rubric criteria for each dimension (for tooltip expectations)
  // Note: TaskRubric is a union of V1 (record) and V2 (with dimensions array)
  const dimensionCriteria = useMemo(() => {
    const rubric = task?.rubric;
    if (!rubric || !('dimensions' in rubric) || !Array.isArray(rubric.dimensions)) {
      return {};
    }
    const criteria: Record<string, DimensionCriterion[]> = {};
    rubric.dimensions.forEach((rubricDim) => {
      if (rubricDim.name && rubricDim.criteria) {
        criteria[rubricDim.name.toLowerCase()] = rubricDim.criteria.map((criterion) => ({
          name: criterion.name,
          descriptor: criterion.descriptor,
          pts: criterion.pts,
          critical: criterion.dimension_critical || false,
        }));
      }
    });
    return criteria;
  }, [task?.rubric]);

  const hasAutogradeReasoning = !!(autogradeReasoning && Object.keys(autogradeReasoning).length > 0);

  const evaluationRequired = taskRequiresEvaluation(task, eventKinds);
  const isGradingRelevant = task.gradingSystem === 'full_evaluation';
  const isOralExam = isOralExamTask(task);
  const hideOralExamSubmissionBlob = isOralExam && shouldHideOralExamSubmissionBlob(task);

  const formErrors = (errors?.taskSubmissions as FieldErrors<Record<string, unknown>> | undefined)?.[task.id];
  const errorProps = formErrors ? { borderColor: '$statusError', borderWidth: 2 } : {};

  // Word count calculation
  const submissionText = task.submission?.text;
  const currentWords = submissionText ? countWords(submissionText) : null;
  const formalReqs = task?.evaluation?.formalCriteria;
  const shouldHaveSubmission = task.type !== 'activity';
  const isExpandable = shouldHaveSubmission && evaluationRequired && task.type !== 'multiple_choice';

  // Form field names
  const evalFieldName = `taskEvaluations.${task.id}.evaluation`;
  const taskDirtyFields = dirtyFields?.taskEvaluations?.[task.id];
  const isTaskDirty = !!taskDirtyFields;

  // Word count status for passing to eval card
  const getWordCountStatus = (): {
    currentWords: number;
    hasRequirement: boolean;
    targetWords?: number;
    isWithinRange?: boolean;
    minWords?: number;
    maxWords?: number;
    tolerancePercent?: string;
  } | null => {
    if (currentWords === null || task?.evaluation?.formalCriteriaSource === 'rubric') {
      return null;
    }
    const bounds = wordCountBounds(formalReqs);
    if (!bounds) {
      return { currentWords, hasRequirement: false };
    }
    const { min: minWords, max: maxWords, target: targetWords } = bounds;
    return {
      currentWords,
      hasRequirement: true,
      targetWords,
      isWithinRange: currentWords >= minWords && currentWords <= maxWords,
      minWords,
      maxWords,
      tolerancePercent: ((formalReqs?.wordcountTolerance ?? 0) * 100).toFixed(0),
    };
  };
  const wordCountStatus = getWordCountStatus();

  // Unified Autosave logic using thinned hook
  // Disable autosave when parent form is submitting or when read-only
  useAutosave({
    saveFunction: async () => {
      const currentTaskData = getValues(watchFormPath);
      await saveEvaluation(currentTaskData);
    },
    onSaveSuccess: () => {
      // Clear dirty state for this task by resetting its fields to current values
      const currentValues = getValues(watchFormPath);
      if (currentValues) {
        // Resetting the whole task path should clear its dirty state
        resetField(watchFormPath, { defaultValue: currentValues });
      }
    },
    watchValue: taskFormData,
    isDirty: isTaskDirty,
    // Disable autosave when read-only, collapsed, disabled (parent submitting), or no data
    enabled: !isReadOnly && isExpandable && isExpanded && !!taskFormData && !isDisabled,
    debounceMs: 2000,
  });

  return (
    <Box testID="submission-element" bg="transparent" flexGrow={1} w="100%" maxWidth={1400} {...errorProps}>
      {/* Task Header Row - now self-contained with its own styling */}
      <ElementHeader
        task={task}
        isExpanded={isExpanded}
        isDisabled={isDisabled}
        toggleElement={toggleElement}
        formErrors={formErrors as Record<string, unknown> | undefined}
        formSubmitted={isSubmitted}
        isExpandable={isExpandable}
        isGradingRelevant={isGradingRelevant}
        evaluationRequired={evaluationRequired}
        applicableDims={applicableDims}
      />

      {/* Expanded Content - whitespace grouping instead of borders */}
      {!!(isExpandable && isExpanded) && (
        <Box ml="$8" mt="$4" pr="$4">
          <Box testID="submission-element-content-expanded" display="flex" flexDirection="column" sx={{ gap: 20 }} pb="$4">
            {/* Main Content Area */}
            <HStack flex={1} space="xl" alignItems="flex-start" minWidth={0}>
              {/* Left Column: Submission + Evaluation */}
              <Box display="flex" flexDirection="column" sx={{ gap: 20 }} flex={1} minWidth={0}>
                {!hideOralExamSubmissionBlob && (
                  <Box>
                    <Text
                      size="xs"
                      color="$textSubtle"
                      fontWeight="medium"
                      textTransform="uppercase"
                      letterSpacing={1}
                      mb="$3"
                    >
                      Student Submission
                    </Text>
                    {/* Block-quote styled submission text */}
                    <Box
                      testID="highlighted-submission-text"
                      borderLeftWidth={3}
                      borderColor="$neutral300"
                      pl="$4"
                      pr="$6"
                    >
                      <HighlightedSubmissionText
                        text={task.submission?.text || ''}
                        highlightConfig={highlightConfig}
                      />
                      {!!(task.submission?.attachments && task.submission.attachments.length > 0) && (
                        <Box mt="$4">
                          <FlatList
                            data={task.submission.attachments as Attachment[]}
                            horizontal
                            keyExtractor={(item) => (item as Attachment).id}
                            renderItem={({ item }) => {
                              const attachment = item as Attachment;
                              return (
                                <AttachmentMenu
                                  key={attachment.id}
                                  attachment={attachment}
                                  allowDelete={false}
                                  onDeleteAttachment={() => {}}
                                  isDeleting={false}
                                  trigger={(triggerProps: Record<string, unknown>) => (
                                    <AttachmentItem
                                      attachment={attachment}
                                      isDeleting={false}
                                      triggerProps={triggerProps}
                                    />
                                  )}
                                />
                              );
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Evaluation section - styled like Student Submission */}
                <Box>
                  <Text
                    size="xs"
                    color="$textSubtle"
                    fontWeight="medium"
                    textTransform="uppercase"
                    letterSpacing={1}
                    mb="$3"
                  >
                    Evaluation
                  </Text>
                  <Box
                    borderLeftWidth={3}
                    borderColor="$neutral300"
                    pl="$4"
                    pr="$6"
                  >
                    {isOralExam ? (
                      <OralExamEvaluationPanel
                        task={task}
                        taskId={task.id}
                        isReadOnly={isReadOnly}
                        isDisabled={isDisabled}
                        isDirty={isTaskDirty}
                        saveStatus={controllerStatus}
                        errorMessage={controllerError}
                        onRetry={() => saveEvaluation(getValues(`taskEvaluations.${task.id}`))}
                        isAutograding={isAutograding}
                        isSaving={controllerStatus === 'saving'}
                      />
                    ) : (
                      <EvaluationCard
                        task={task}
                        applicableDims={applicableDims}
                        evalFieldName={evalFieldName}
                        isAutograding={isAutograding}
                        isSaving={controllerStatus === 'saving'}
                        isReadOnly={isReadOnly}
                        isDisabled={isDisabled}
                        evaluationRequired={evaluationRequired}
                        wordCountStatus={wordCountStatus}
                        isGradingRelevant={isGradingRelevant}
                        isDirty={isTaskDirty}
                        saveStatus={controllerStatus}
                        errorMessage={controllerError}
                        onRetry={() => saveEvaluation(getValues(`taskEvaluations.${task.id}`))}
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Chaptered oral cards already carry question text + spoken answer;
                  the description rail would steal the width those panes need.
                  Teams / flat / pre-chapter exam sessions keep the rail. */}
              {!hideOralExamSubmissionBlob && (
                <Box
                  testID="submission-task-context-rail"
                  w={400}
                  flexShrink={0}
                  bg="$white"
                  borderRadius="$xl"
                  p="$5"
                  shadowColor="$black"
                  shadowOffset={{ width: 0, height: 2 }}
                  shadowOpacity={0.08}
                  shadowRadius={8}
                  elevation={2}
                  sx={{
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    position: 'relative' as any, // Fallback for native
                    // @ts-expect-error - sticky works on web
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    position: 'sticky' as any,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    top: 20 as any,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    height: 'calc(100vh - 140px)' as any,
                  }}
                  display="flex"
                  flexDirection="column"
                >
                  <TaskContextPreview
                    task={task}
                    applicableDims={applicableDims}
                    autogradeData={autogradeReasoning}
                    showAutogradeReasoning={hasAutogradeReasoning}
                    dimensionCriteria={dimensionCriteria}
                  />
                </Box>
              )}
            </HStack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SubmissionElement;
